import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useQuote } from "../context/QuoteContext";

const nav = [
  { to: "/", label: "Home" },
  { to: "/products", label: "Products" },
  { to: "/industries", label: "Industries" },
  { to: "/about", label: "About Us" },
  { to: "/clients", label: "Our Clients" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { openQuote } = useQuote();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobileOpen(false), [pathname]);

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || mobileOpen ? "bg-white/90 backdrop-blur-xl border-b border-[#0A1128]/10 py-3" : "bg-transparent py-5"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="header-logo">
          <img src="/clinixport-logo.png" alt="Clinixport Global logo" className="h-10 sm:h-11 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-9">
          {nav.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`text-sm font-semibold tracking-wide hover-underline transition-colors ${
                pathname === n.to ? "text-[#1F6FEB]" : "text-[#0A1128] hover:text-[#1F6FEB]"
              }`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => openQuote()}
            data-testid="header-request-quote"
            className="hidden sm:inline-flex bg-[#1F6FEB] text-white hover:bg-[#1556B8] transition-colors px-6 py-3 font-bold text-xs tracking-widest uppercase"
          >
            Request a Quote
          </button>
          <button
            className="lg:hidden text-[#0A1128] p-1"
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-white border-t border-[#0A1128]/10 mt-3"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-5 flex flex-col gap-1">
              {nav.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  className="py-3 text-lg font-bold text-[#0A1128] border-b border-[#0A1128]/5"
                >
                  {n.label}
                </Link>
              ))}
              <button
                onClick={() => openQuote()}
                data-testid="mobile-request-quote"
                className="mt-4 bg-[#1F6FEB] text-white px-6 py-4 font-bold text-sm tracking-widest uppercase"
              >
                Request a Quote
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
